import { type QueryClient } from '@tanstack/react-query';
import type { QuerySessionResponse } from '../../contracts';
import {
  abortArtifactGeneration,
  beginArtifactGeneration,
  completeArtifactGeneration,
  resetArtifactGenerationCoordinatorForTests,
  stopAllArtifactGenerations,
} from './artifact-generation-coordinator';
import { markAbortAsSupersedingStream } from './generation-abort-reasons';

export type GenerationCancellationReason = 'user' | 'superseded' | 'cleanup' | 'recovery';

export type GenerationHandle = {
  sessionId: string;
  generationId: number;
  controller: AbortController;
};

export type DocumentFlowDispatchHandle = {
  release(): void;
  bindDocument(documentId: string): void;
};

type ActiveGeneration = GenerationHandle & {
  cancellationReason?: GenerationCancellationReason;
  postActionsCompleted: boolean;
  noteRequestAccepted: boolean;
  noteRequestEnded: boolean;
};

let nextGenerationId = 0;
const activeGenerations = new Map<string, ActiveGeneration>();
const pendingNoteRequests = new Set<string>();
const noteRequestListeners = new Set<(endedBeforeAcceptance: boolean) => void>();
const pendingDocumentRequests = new Map<string, Set<symbol>>();
const documentFlowDispatchHolds = new Map<string, Map<symbol, { documentId?: string }>>();
const postNoteDocumentDispatchHolds = new Map<string, Set<symbol>>();
const documentRequestListeners = new Set<(endedBeforeAcceptance?: boolean) => void>();

const hasSessionDocumentHold = (sessionId: string): boolean =>
  (documentFlowDispatchHolds.get(sessionId)?.size ?? 0) > 0 ||
  (postNoteDocumentDispatchHolds.get(sessionId)?.size ?? 0) > 0;

const hasPendingDocumentDispatch = (): boolean =>
  pendingDocumentRequests.size > 0 ||
  documentFlowDispatchHolds.size > 0 ||
  postNoteDocumentDispatchHolds.size > 0;

const notifyDocumentRequestListeners = (endedBeforeAcceptance?: boolean): void => {
  documentRequestListeners.forEach((listener) => listener(endedBeforeAcceptance));
};

const holdDocumentDispatch = (
  holds: Map<string, Set<symbol>>,
  sessionId: string
): (() => void) => {
  const token = Symbol();
  const tokens = holds.get(sessionId) ?? new Set<symbol>();
  tokens.add(token);
  holds.set(sessionId, tokens);
  notifyDocumentRequestListeners();

  let released = false;
  return () => {
    if (released) return;
    released = true;
    tokens.delete(token);
    if (tokens.size === 0 && holds.get(sessionId) === tokens) {
      holds.delete(sessionId);
    }
    notifyDocumentRequestListeners();
  };
};

const releaseOneDocumentFlowHold = (sessionId: string, documentId: string): void => {
  const holds = documentFlowDispatchHolds.get(sessionId);
  const matched = [...(holds ?? [])].find(([, hold]) => hold.documentId === documentId);
  if (!holds || !matched) return;
  holds.delete(matched[0]);
  if (holds.size === 0 && documentFlowDispatchHolds.get(sessionId) === holds) {
    documentFlowDispatchHolds.delete(sessionId);
  }
};

export const holdDocumentFlowDispatch = (
  sessionId: string
): DocumentFlowDispatchHandle => {
  const holds =
    documentFlowDispatchHolds.get(sessionId) ??
    new Map<symbol, { documentId?: string }>();
  const token = Symbol();
  const hold: { documentId?: string } = {};
  holds.set(token, hold);
  documentFlowDispatchHolds.set(sessionId, holds);
  notifyDocumentRequestListeners();

  let released = false;
  return {
    bindDocument: (documentId) => {
      if (released) return;
      hold.documentId = documentId;
    },
    release: () => {
      if (released) return;
      released = true;
      holds.delete(token);
      if (holds.size === 0 && documentFlowDispatchHolds.get(sessionId) === holds) {
        documentFlowDispatchHolds.delete(sessionId);
      }
      notifyDocumentRequestListeners();
    },
  };
};

export const holdPostNoteDocumentDispatch = (sessionId: string): (() => void) =>
  holdDocumentDispatch(postNoteDocumentDispatchHolds, sessionId);

export const trackDocumentRequest = (
  sessionId: string,
  documentId: string
): { accepted(): void; ended(): void } => {
  const token = Symbol();
  const tokens = pendingDocumentRequests.get(sessionId) ?? new Set<symbol>();
  tokens.add(token);
  pendingDocumentRequests.set(sessionId, tokens);
  notifyDocumentRequestListeners();

  let settled = false;
  const settle = (endedBeforeAcceptance: boolean): void => {
    if (settled) return;
    settled = true;
    tokens.delete(token);
    if (tokens.size === 0) pendingDocumentRequests.delete(sessionId);
    releaseOneDocumentFlowHold(sessionId, documentId);
    notifyDocumentRequestListeners(endedBeforeAcceptance);
  };
  return {
    accepted: () => settle(false),
    ended: () => settle(true),
  };
};

export const hasPendingDocumentRequests = (): boolean => hasPendingDocumentDispatch();

export const waitForPendingDocumentRequests = (
  deadlineAt: number,
  needsDocumentGeneration: (sessionId: string) => Promise<boolean>
): Promise<'none' | 'accepted' | 'ended' | 'timedOut'> => {
  if (!hasPendingDocumentDispatch() && !hasAcceptedOpenNoteGeneration()) {
    return Promise.resolve('none');
  }

  return new Promise((resolve) => {
    let settled = false;
    let sawEndedRequest = false;
    let sawAcceptedRequest = false;
    const needChecks = new Map<
      string,
      { generationId: number; state: 'checking' | 'needed' | 'ignored' }
    >();
    const settle = (outcome: 'none' | 'accepted' | 'ended' | 'timedOut') => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      documentRequestListeners.delete(onChange);
      resolve(outcome);
    };

    const onChange = (endedBeforeAcceptance?: boolean) => {
      if (endedBeforeAcceptance === true) sawEndedRequest = true;
      if (endedBeforeAcceptance === false) sawAcceptedRequest = true;
      void check();
    };

    const check = async (): Promise<void> => {
      if (settled) return;
      const pendingChecks: Promise<void>[] = [];
      const generationBlocks = new Set<string>();

      activeGenerations.forEach((generation, sessionId) => {
        if (
          !generation.noteRequestAccepted ||
          generation.noteRequestEnded ||
          generation.controller.signal.aborted ||
          hasSessionDocumentHold(sessionId)
        ) {
          return;
        }

        const currentCheck = needChecks.get(sessionId);
        if (currentCheck?.generationId === generation.generationId) {
          if (currentCheck.state === 'needed') generationBlocks.add(sessionId);
          if (currentCheck.state === 'checking') {
            pendingChecks.push(Promise.resolve());
            generationBlocks.add(sessionId);
          }
          return;
        }

        needChecks.set(sessionId, {
          generationId: generation.generationId,
          state: 'checking',
        });
        generationBlocks.add(sessionId);
        pendingChecks.push(
          needsDocumentGeneration(sessionId)
            .catch(() => true)
            .then((needsGeneration) => {
              if (settled) return;
              const latest = activeGenerations.get(sessionId);
              if (latest?.generationId !== generation.generationId) {
                if (needChecks.get(sessionId)?.generationId === generation.generationId) {
                  needChecks.delete(sessionId);
                }
                void check();
                return;
              }
              needChecks.set(sessionId, {
                generationId: generation.generationId,
                state: needsGeneration ? 'needed' : 'ignored',
              });
              void check();
            })
        );
      });

      const hasHolds =
        documentFlowDispatchHolds.size > 0 || postNoteDocumentDispatchHolds.size > 0;
      const hasRequests = pendingDocumentRequests.size > 0;
      const needsCheckPending = [...needChecks.values()].some(
        (entry) => entry.state === 'checking'
      );
      const generationStillNeedsDocuments = [...generationBlocks].some((sessionId) => {
        const entry = needChecks.get(sessionId);
        const generation = activeGenerations.get(sessionId);
        return (
          entry?.state === 'needed' &&
          generation?.generationId === entry.generationId &&
          !generation.controller.signal.aborted
        );
      });

      if (
        !hasHolds &&
        !hasRequests &&
        !needsCheckPending &&
        !generationStillNeedsDocuments
      ) {
        settle(sawEndedRequest ? 'ended' : sawAcceptedRequest ? 'accepted' : 'none');
      }
      if (pendingChecks.length > 0) await Promise.all(pendingChecks);
    };

    const timer = setTimeout(
      () => settle('timedOut'),
      Math.max(0, deadlineAt - Date.now())
    );
    documentRequestListeners.add(onChange);
    void check();
  });
};

export const hasPendingNoteRequests = (): boolean =>
  pendingNoteRequests.size > 0 ||
  [...activeGenerations.values()].some(
    (generation) => !generation.noteRequestAccepted && !generation.noteRequestEnded
  );

export const hasAcceptedOpenNoteGeneration = (): boolean =>
  [...activeGenerations.values()].some(
    (generation) =>
      generation.noteRequestAccepted &&
      !generation.noteRequestEnded &&
      !generation.controller.signal.aborted
  );

const notifyNoteRequestListeners = (endedBeforeAcceptance = false): void => {
  noteRequestListeners.forEach((listener) => listener(endedBeforeAcceptance));
};

export const markSessionNoteRequestPending = (sessionId: string): void => {
  if (pendingNoteRequests.has(sessionId)) return;
  pendingNoteRequests.add(sessionId);
  notifyNoteRequestListeners();
};

export const clearSessionNoteRequestPending = (sessionId: string): void => {
  if (!pendingNoteRequests.delete(sessionId)) return;
  notifyNoteRequestListeners(true);
};

export const markSessionGenerationRequestAccepted = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): void => {
  if (!isLatestGeneration(handle)) return;
  const current = activeGenerations.get(handle.sessionId);
  if (
    !current ||
    current.generationId !== handle.generationId ||
    current.controller.signal.aborted ||
    current.noteRequestAccepted ||
    current.noteRequestEnded
  ) {
    return;
  }
  current.noteRequestAccepted = true;
  notifyNoteRequestListeners();
  notifyDocumentRequestListeners();
};

export const waitForPendingNoteRequests = (
  deadlineAt: number
): Promise<'none' | 'accepted' | 'ended' | 'timedOut'> => {
  if (!hasPendingNoteRequests()) return Promise.resolve('none');

  return new Promise((resolve) => {
    let sawEndedRequest = false;
    const settle = (outcome: 'accepted' | 'ended' | 'timedOut') => {
      clearTimeout(timer);
      noteRequestListeners.delete(onChange);
      resolve(outcome);
    };
    const onChange = (endedBeforeAcceptance: boolean) => {
      sawEndedRequest ||= endedBeforeAcceptance;
      if (!hasPendingNoteRequests()) {
        settle(sawEndedRequest ? 'ended' : 'accepted');
      }
    };

    noteRequestListeners.add(onChange);
    const timer = setTimeout(
      () => settle('timedOut'),
      Math.max(0, deadlineAt - Date.now())
    );
  });
};

export const beginSessionGeneration = ({
  queryClient,
  queryKey,
  sessionId,
  outgoingContent,
}: {
  queryClient: QueryClient;
  queryKey: string;
  sessionId: string;
  outgoingContent?: string | null;
}): GenerationHandle => {
  const previous = activeGenerations.get(sessionId);
  const previousEndedBeforeAcceptance =
    previous !== undefined && !previous.noteRequestAccepted && !previous.noteRequestEnded;
  if (previous) {
    previous.cancellationReason = 'superseded';
    previous.noteRequestEnded ||= previousEndedBeforeAcceptance;
  }

  const latestSession = queryClient.getQueryData<QuerySessionResponse>([
    queryKey,
    { sessionId },
  ]);
  const existingController = latestSession?.abortController;
  if (existingController && !existingController.signal.aborted) {
    markAbortAsSupersedingStream(existingController);
    existingController.abort();
  }

  const controller = new AbortController();
  queryClient.setQueryData<QuerySessionResponse>([queryKey, { sessionId }], (oldData) =>
    oldData ? { ...oldData, abortController: controller } : undefined
  );
  const handle: ActiveGeneration = {
    sessionId,
    generationId: ++nextGenerationId,
    controller,
    postActionsCompleted: false,
    noteRequestAccepted: false,
    noteRequestEnded: false,
  };
  pendingNoteRequests.delete(sessionId);
  activeGenerations.set(sessionId, handle);
  beginArtifactGeneration({
    key: { kind: 'note', sessionId },
    outgoingContent:
      outgoingContent === undefined
        ? (latestSession?.consult_note?.result ?? null)
        : outgoingContent,
    controller,
  });
  notifyNoteRequestListeners(previousEndedBeforeAcceptance);
  notifyDocumentRequestListeners();
  return handle;
};

export const getCurrentSessionGeneration = (
  sessionId: string
): GenerationHandle | undefined => activeGenerations.get(sessionId);

/** Test compatibility for legacy action tests that did not carry a session. */
export const getOnlyActiveGenerationForTests = (): GenerationHandle | undefined =>
  activeGenerations.size === 1 ? activeGenerations.values().next().value : undefined;

export const isLatestGeneration = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): boolean =>
  activeGenerations.get(handle.sessionId)?.generationId === handle.generationId;

export const isCurrentGeneration = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): boolean => {
  const current = activeGenerations.get(handle.sessionId);
  return isLatestGeneration(handle) && !current?.controller.signal.aborted;
};

export const assertCurrentGeneration = (handle: GenerationHandle): void => {
  if (isCurrentGeneration(handle)) return;
  const error = new Error('Side-by-side generation is no longer current');
  error.name = 'AbortError';
  throw error;
};

export const stopSessionGeneration = (
  sessionId: string,
  reason: GenerationCancellationReason = 'user'
): boolean => {
  const current = activeGenerations.get(sessionId);
  const hadPendingRequest = pendingNoteRequests.delete(sessionId);
  if (!current) {
    if (hadPendingRequest) notifyNoteRequestListeners(true);
    return false;
  }
  const endedBeforeAcceptance = !current.noteRequestAccepted && !current.noteRequestEnded;
  current.cancellationReason = reason;
  current.noteRequestEnded ||= endedBeforeAcceptance;
  if (!current.controller.signal.aborted) {
    current.controller.abort(reason);
  }
  abortArtifactGeneration({ kind: 'note', sessionId }, reason);
  notifyNoteRequestListeners(endedBeforeAcceptance || hadPendingRequest);
  notifyDocumentRequestListeners();
  return true;
};

export const stopAllSessionGenerations = (
  reason: GenerationCancellationReason = 'cleanup'
): void => {
  let endedBeforeAcceptance = pendingNoteRequests.size > 0;
  [...activeGenerations.values()].forEach((generation) => {
    if (!generation.noteRequestAccepted && !generation.noteRequestEnded) {
      generation.noteRequestEnded = true;
      endedBeforeAcceptance = true;
    }
    generation.cancellationReason = reason;
    if (!generation.controller.signal.aborted) {
      generation.controller.abort(reason);
    }
    abortArtifactGeneration({ kind: 'note', sessionId: generation.sessionId }, reason);
  });
  activeGenerations.clear();
  pendingNoteRequests.clear();
  const endedDocumentRequest = pendingDocumentRequests.size > 0;
  pendingDocumentRequests.clear();
  documentFlowDispatchHolds.clear();
  postNoteDocumentDispatchHolds.clear();
  stopAllArtifactGenerations(reason);
  notifyNoteRequestListeners(endedBeforeAcceptance);
  if (endedDocumentRequest) notifyDocumentRequestListeners(true);
  else notifyDocumentRequestListeners();
};

export const getGenerationCancellationReason = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): GenerationCancellationReason | undefined => {
  const current = activeGenerations.get(handle.sessionId);
  if (current?.generationId !== handle.generationId) return 'superseded';
  return current.cancellationReason;
};

export const claimGenerationPostActions = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): boolean => {
  const current = activeGenerations.get(handle.sessionId);
  if (
    current?.generationId !== handle.generationId ||
    current.controller.signal.aborted ||
    current.postActionsCompleted
  ) {
    return false;
  }
  current.postActionsCompleted = true;
  return true;
};

export const finishSessionGeneration = (
  handle: Pick<GenerationHandle, 'sessionId' | 'generationId'>
): void => {
  const current = activeGenerations.get(handle.sessionId);
  if (current?.generationId === handle.generationId) {
    const endedBeforeAcceptance =
      !current.noteRequestAccepted && !current.noteRequestEnded;
    current.noteRequestEnded ||= endedBeforeAcceptance;
    completeArtifactGeneration({ kind: 'note', sessionId: handle.sessionId });
    activeGenerations.delete(handle.sessionId);
    notifyNoteRequestListeners(endedBeforeAcceptance);
    notifyDocumentRequestListeners();
  }
};

/** Test-only registration without a React Query cache. */
export const registerSessionGenerationForTests = (
  sessionId: string,
  generationId = ++nextGenerationId
): GenerationHandle => {
  const previous = activeGenerations.get(sessionId);
  const previousEndedBeforeAcceptance =
    previous !== undefined && !previous.noteRequestAccepted && !previous.noteRequestEnded;
  if (previous) previous.noteRequestEnded ||= previousEndedBeforeAcceptance;
  const controller = new AbortController();
  const handle: ActiveGeneration = {
    sessionId,
    generationId,
    controller,
    postActionsCompleted: false,
    noteRequestAccepted: false,
    noteRequestEnded: false,
  };
  pendingNoteRequests.delete(sessionId);
  activeGenerations.set(sessionId, handle);
  nextGenerationId = Math.max(nextGenerationId, generationId);
  notifyNoteRequestListeners(previousEndedBeforeAcceptance);
  notifyDocumentRequestListeners();
  return handle;
};

/** Test-only reset for the module-level client generation registry. */
export const resetGenerationCoordinatorForTests = (): void => {
  let endedBeforeAcceptance = pendingNoteRequests.size > 0;
  activeGenerations.forEach((generation) => {
    if (!generation.noteRequestAccepted && !generation.noteRequestEnded) {
      generation.noteRequestEnded = true;
      endedBeforeAcceptance = true;
    }
    if (!generation.controller.signal.aborted) generation.controller.abort('cleanup');
  });
  resetArtifactGenerationCoordinatorForTests();
  activeGenerations.clear();
  pendingNoteRequests.clear();
  const endedDocumentRequest = pendingDocumentRequests.size > 0;
  pendingDocumentRequests.clear();
  documentFlowDispatchHolds.clear();
  postNoteDocumentDispatchHolds.clear();
  nextGenerationId = 0;
  notifyNoteRequestListeners(endedBeforeAcceptance);
  if (endedDocumentRequest) notifyDocumentRequestListeners(true);
  else notifyDocumentRequestListeners();
};
